<script setup>
import { onMounted, ref, defineProps, defineEmits, defineExpose } from 'vue';

defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  type: {
    type: String,
    default: 'text',
    validator: (value) => ['text', 'number', 'date', 'datetime-local', 'time'].includes(value)
  },
  id: {
    type: String,
    required: true
  },
  placeholder: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  pattern: {
    type: String,
    default: ''
  },
  isTextarea: {
    type: Boolean,
    default: false
  },
  rows: {
    type: Number,
    default: 3
  },
  autocomplete: {
    type: String,
    default: 'off'
  }
});

defineEmits(['update:modelValue']);

const input = ref(null);

onMounted(() => {
  if (input.value?.hasAttribute('autofocus')) {
    input.value?.focus();
  }
});

defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <div class="text-input">
    <div v-if="$slots.icon" class="text-input__icon-container">
      <slot name="icon"></slot>
    </div>
    
    <textarea
      v-if="isTextarea"
      ref="input"
      :value="modelValue"
      @input="$emit('update:modelValue', $event.target.value)"
      :id="id"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :rows="rows"
      :autocomplete="autocomplete"
      :class="[
        'text-input__field',
        'text-input__textarea',
        $slots.icon ? 'text-input__field--with-icon' : '',
        disabled ? 'text-input__field--disabled' : ''
      ]"
    ></textarea>
    
    <input
      v-else
      ref="input"
      :type="type"
      :value="modelValue"
      @input="$emit('update:modelValue', $event.target.value)"
      :id="id"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :autocomplete="autocomplete"
      v-bind="pattern ? { pattern } : {}"
      :class="[
        'text-input__field',
        $slots.icon ? 'text-input__field--with-icon' : '',
        disabled ? 'text-input__field--disabled' : '',
        type === 'date' ? 'text-input__field--date' : ''
      ]"
    />
  </div>
</template>

<style scoped lang="postcss">
.text-input {
  @apply relative isolate;
}

.text-input__icon-container {
  @apply absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none
    text-white/80;
}

.text-input__field {
  @apply truncate w-full px-4 py-2.5 text-sm rounded-[13px] h-11
    bg-echoes-background-black
   
    border border-echoes-border-gray
    
    transition-all duration-200 ease-in-out;
  
  &:hover {
    @apply bg-echoes-background-black
      border-white/20
      text-white
      ;
  }

  &:focus {
    @apply bg-echoes-background-black border-echoes-primary outline-none
  }

  &::placeholder {
    @apply transition-all duration-200 text-white/50 hover:text-white/80;
  }

  &:focus::placeholder {
    @apply opacity-0 translate-x-1;
  }
}

.text-input__field--with-icon {
  @apply pl-11;
}

.text-input__field--disabled {
  @apply opacity-60 cursor-not-allowed;

  &:hover {
    @apply bg-echoes-background-black
      border-echoes-border-gray
     
      shadow-none;
  }
}

.text-input__textarea {
  @apply resize-y min-h-[80px];
}

.text-input__field--date {
  @apply cursor-pointer;

  &::-webkit-calendar-picker-indicator {
    @apply cursor-pointer opacity-70 hover:opacity-100 transition-opacity;
    filter: invert(1) brightness(0.9);
  }

  &::-webkit-datetime-edit {
    @apply cursor-text;
  }
}
</style>